import {
    Choice,
    Condition,
    Identifier,
    Info,
    Question,
    QuestionType,
    Result,
    Subquestion,
} from "features/wizard/schema.ts"

// --- Ids ---
// All ids in a schema must be unique in that schema; and question ids are used
// to generate facts by the engine.
// So some type juggling is done here to ensure that there are no collisions
// and everything works and is foolproof, provided the fools aren't too clever.

/**
 * ResultId is a branded type for uniquely identifying results.
 * It adds a "result/" namespace to avoid collisions with question/fact ids.
 */
type ResultId = string & { kind: "resultId" }

/**
 * Create a typed result ID.
 *
 * Example:
 * const soupId = resultId("soup")
 */
export const resultId = (id: string) => `result/${cleanId(id)}` as ResultId

/**
 * Validate and sanitise an id. Internal use only.
 */
const cleanId = (id: string) => {
    if (id.indexOf("/") !== -1) {
        throw new Error("id must not contain a slash")
    }
    return id as Identifier
}

// --- Facts ---
/**
 * Defines all possible facts and their answers for your schema.
 *
 * It is very important to use this, as it helps/ensures that there are no loose ids.
 *
 * Example:
 * const facts = makeFacts({
 *   diet: ["healthy", "vegetarian", "lchf"],
 *   allergies: ["gluten", "nuts"]
 * });
 *
 * facts.diet.fact -> "diet"
 * facts.diet.answer.healthy -> "healthy"
 */
export const makeFacts = <T extends Record<string, readonly string[]>>(
    facts: T,
) => {
    return Object.fromEntries(
        Object.entries(facts).map(([fact, answers_]) => [
            fact,
            {
                fact: fact as FactString,
                answer: Object.fromEntries(
                    answers_.map((a) => [a, a as AnswerString]),
                ) as { [K in T[keyof T][number]]: AnswerString },
            },
        ]),
    ) as {
        [K in keyof T]: {
            fact: FactString
            answer: { [P in T[K][number]]: AnswerString }
        }
    }
}

export function makeAnswerIndex<T extends Record<string, CompleteFact>>(
    facts: T,
) {
    const result_: any = {}
    for (const [factName, fact] of Object.entries(facts)) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        result_[factName] = {}
        for (const answerKey in fact.answer) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            result_[factName][answerKey] = [fact, fact.answer[answerKey]]
        }
    }
    return result_ as {
        [K in keyof T]: {
            [A in keyof T[K]["answer"]]: [T[K], T[K]["answer"][A]]
        }
    }
}

// --- Questions ---
type OptionAugment = {
    answer: AnswerString
    title: string
    info?: Info
}

function buildOptions(fact: CompleteFact, augmentOptions: OptionAugment[]) {
    if (augmentOptions.length === 0) {
        return Object.values(fact.answer).map(
            (answer): Choice => ({
                id: answer as unknown as Identifier,
                title: answer,
                info: {},
            }),
        )
    }
    const seenAnswers: Record<AnswerString, boolean | undefined> = {}
    const options = augmentOptions.map((ao) => {
        if (seenAnswers[ao.answer]) {
            throw new Error(`Duplicate answer: ${ao.answer}`)
        }
        if (!fact.answer[ao.answer]) {
            throw new Error(
                `Invalid answer: ${ao.answer} is not part of fact ${fact.fact}`,
            )
        }
        seenAnswers[ao.answer] = true
        return {
            id: ao.answer as unknown as Identifier,
            title: ao.title,
            info: ao.info ?? {},
        }
    })
    if (
        augmentOptions.length !==
        Object.values(seenAnswers).filter(Boolean).length
    ) {
        throw new Error("missing answer")
    }

    return options
}

/**
 * Builds a question.
 *
 * Internal helper. Use `oneAnswerQuestion` or `anyThatApplyQuestion` instead.
 */
const question =
    (type: QuestionType) =>
    (
        fact: CompleteFact,
        title: string,
        info: Info,
        augmentOptions: OptionAugment[],
        subquestions: Subquestion[],
    ): Question => ({
        id: cleanId(fact.fact),
        type,
        title,
        info,
        options: buildOptions(fact, augmentOptions),
        subquestions,
    })

/**
 * Creates a single-choice question.
 */
export const oneAnswerQuestion = question("oneAnswer")

/**
 * Creates a multiple-choice question.
 */
export const anyThatApplyQuestion = question("anyThatApply")

/**
 * Creates a conditional subquestion.
 *
 * Internal helper. Use `subquestionOneAnswer` or `subquestionAnyThatApply`.
 */
const subquestion =
    (type: QuestionType) =>
    (
        fact: CompleteFact,
        title: string,
        info: Info,
        augmentOptions: OptionAugment[],
        when: Test,
    ): Subquestion => ({
        id: cleanId(fact.fact),
        type,
        title,
        info,
        options: buildOptions(fact, augmentOptions),
        when: conditionFromTest(when),
    })

/**
 * Creates a single-choice subquestion, shown only if a condition is met.
 */
export const subquestionOneAnswer = subquestion("oneAnswer")

/**
 * Creates a multi-choice subquestion, shown only if a condition is met.
 */
export const subquestionAnyThatApply = subquestion("anyThatApply")

// --- Results ---

/**
 * Defines a possible result of the wizard.
 *
 * Example:
 * const myResult = result(soupId, {
 *   title: "Soup",
 *   info: { description: "Fallback result." },
 *   when: when.questionsAnsweredAndAll()
 * });
 */
export function result(
    id: ResultId,
    {
        title,
        info,
        when,
    }: {
        title: string
        info?: Info
        when: Test
    },
): Result {
    return {
        id: id as unknown as Identifier,
        title,
        info: info ?? {},
        when: conditionFromTest(when),
    }
}

// --- Conditions ---

/**
 * Types used to define conditional logic.
 */
type FactString = string & { kind: "fact" }
type AnswerString = string & { kind: "answer" }
type CompleteFact = {
    fact: FactString
    answer: Record<string, AnswerString>
}
type Test =
    | {
          fact: FactString
          operator: string
          value: AnswerString | AnswerString[]
      }
    | { not: Test }
    | { all: Test[] }
    | { any: Test[] }
/**
 * Internal helper to generate fact condition functions.
 */
const facter =
    (operator: string) =>
    (fact: FactString, value: AnswerString | AnswerString[]) => ({
        fact,
        operator,
        value,
    })

/**
 * Combine multiple conditions with AND.
 */
function all(...conditions: Test[]): Test {
    return {
        all: conditions,
    }
}

/**
 * Combine multiple conditions with OR.
 */
function any(...conditions: Test[]): Test {
    return {
        any: conditions,
    }
}

function not(test: Test): Test {
    return {
        not: test,
    }
}

const always: Test = {
    all: [],
}

const never: Test = {
    any: [],
}

function answered(fact: FactString): Test {
    return not({
        fact,
        operator: "equal",
        value: undefined as unknown as AnswerString,
    })
}

const anyAnswer = facter("someFact:in")

function noAnswer(fact: FactString, answers_: AnswerString[]): Test {
    return all(answered(fact), not(anyAnswer(fact, answers_)))
}

function conditionFromTest(test: Test): Condition {
    if ("all" in test) {
        return test
    }
    if ("fact" in test) {
        return {
            all: [
                {
                    fact: test.fact,
                    operator: test.operator,
                    value: test.value,
                },
            ],
        }
    }
    if ("not" in test) {
        return test
    }
    if ("any" in test) {
        return test
    }

    throw new Error("Unsupported test structure")
}

export function answers(
    ...conditions: readonly [CompleteFact, AnswerString][]
): Test {
    for (const [fact, answer] of conditions) {
        if (!fact.answer[answer]) {
            throw new Error(
                `Mismatched fact/answer pair: ${answer} is not part of fact ${fact.fact}`,
            )
        }
    }

    return all(
        ...conditions.map(([fact, answer]) => when.equal(fact.fact, answer)),
    )
}
/**
 * An object providing various conditions or checks to evaluate facts or data.
 *
 * These are used to define when a result should apply or a subquestion should be shown.
 * Conditions are structured as composable test expressions that map to the schema logic
 * and can also be serialised into compatible rule engine formats.
 */
export const when = {
    /**
     * Check if a fact does NOT contain a value.
     *
     * Useful for multi-select facts where a certain value must not be included.
     *
     * Example: `doesNotContain(fact.diet.fact, fact.diet.answer.vegetarian)`
     */
    doesNotContain: facter("doesNotContain"),

    /**
     * Check if a fact exactly matches a value.
     *
     * Use for single-choice or string-based facts where you want strict equality.
     *
     * Example: `equal(fact.diet.fact, fact.diet.answer.lchf)`
     */
    equal: facter("equal"),

    /**
     * Check if a fact-array contains a value.
     *
     * Similar to `equal`, but works on multi-answer facts like checkboxes.
     *
     * Example: `contains(fact.allergies.fact, fact.allergies.answer.nuts)`
     */
    contains: facter("contains"),

    /**
     * Check if the fact's value exists in the provided array of values.
     *
     * This is reverse containment — useful for combining multiple valid answers.
     *
     * Example: `in(fact.region.fact, [region.EU, region.NA])`
     */
    in: facter("in"),

    /**
     * Check if the fact array includes *any* of the provided answers.
     *
     * Only applies to array-like facts. Equivalent to "intersects".
     *
     * Note:
     * - Implicitly requires the fact to be answered — if unanswered, this will be false.
     * - To express the opposite, use {@link noAnswer} instead.
     *   Using `not(anyAnswer(...))` will also match unanswered facts
     *   because the domain logic is effectively three-valued:
     *   "yes", "no", and "missing". This subtlety often leads to bugs.
     *
     * Example: `anyAnswer(fact.tags.fact, [tag.A, tag.B])`
     */
    anyAnswer,

    /**
     * Check if the fact has *any* answer at all (i.e. it is not unanswered).
     *
     * Works for both single- and multi-select facts.
     *
     * Note:
     * - This only distinguishes between "answered" and "missing".
     * - It does not check what the answer *is* — for that, use `equal`, `anyAnswer`, etc.
     * - Be aware of the three-valued logic: "yes", "no", and "missing".
     *   `answered` is true for both "yes" and "no", but false for "missing".
     *
     * Example: `answered(fact.hungerLevel.fact)`
     */
    answered,

    /**
     * Check if the fact has an answer, and that answer includes *none* of the provided options.
     *
     * Only applies to multi-select facts.
     *
     * Note:
     * - This is different from `not(anyAnswer(...))`, because `noAnswer` requires the fact
     *   to be explicitly answered. With `not(anyAnswer(...))`, an unanswered fact would also pass.
     * - Think of the logic as three-valued: "yes", "no", "missing".
     *   `noAnswer` is only true for "no" (explicitly answered, but none match).
     *
     * Example: `noAnswer(fact.diet.fact, [diet.LCHF, diet.keto])`
     *   → true if diet is answered but includes none of the above.
     */
    noAnswer,

    /**
     * Combine multiple conditions with logical AND.
     *
     * All provided conditions must be true.
     *
     * Example: `all(condA, condB)`
     */
    all,

    /**
     * Combine multiple conditions with logical OR.
     *
     * At least one of the conditions must be true.
     *
     * Example: `any(condA, condB)`
     */
    any,

    /**
     * Invert a condition.
     *
     * Example: `not(equal(fact.diet.fact, fact.diet.answer.vegetarian))`
     */
    not,

    /**
     * A condition that is always true.
     *
     * Use when a result or subquestion should always be shown.
     *
     * Internally represented as `{ all: [] }`, which is vacuously true.
     */
    always,

    /**
     * A condition that is always false.
     *
     * Use when you want to temporarily disable a rule or subquestion.
     *
     * Internally represented as `{ any: [] }`, which is vacuously false.
     */
    never,

    /**
     * Requires that all questions have been answered and that the provided
     * conditions are also true.
     *
     * This is primarily used for results that depend on full completion of the wizard.
     *
     * Never use this for subquestions — they can be shown during partial progress.
     *
     * Example:
     * ```ts
     * result(resultId("high-protein"), {
     *   title: "High protein recommendation",
     *   info: { ... },
     *   when: when.questionsAnsweredAndAll(
     *     when.equal(fact.diet.fact, fact.diet.answer.lchf),
     *     when.contains(fact.preferences.fact, fact.preferences.answer.protein)
     *   )
     * })
     * ```
     */
    questionsAnsweredAndAll(...conditions: Test[]): Test {
        return all(
            {
                fact: "$nextQuestion" as FactString,
                operator: "equal",
                value: null as unknown as AnswerString,
            },
            ...conditions,
        )
    },
} as const
